/**
 * Run-interrupted recovery card.
 *
 * The run's pod died mid-flight (lease expired on a cross-pod reconnect; see
 * ``docs/DESIGN_MULTIPOD_RUN_LIFECYCLE.md`` D4). There is no live task left to
 * tail, so instead of a frozen "Agent is working…" the transcript renders this
 * card with a Retry button that re-fires the user's last turn.
 *
 * Chrome mirrors {@link AssetCard} (the object-cards spec) — white card, 3px
 * left rail, same tokens and type scale — with the header icon in the error
 * palette to signal the failure state.
 *
 * Rendered only when ``interruptedRun.retryable`` is set — a non-retryable
 * interruption (none today) would surface differently.
 */

import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { borderRadius, shadows } from '@verantos-dev/shared-ui-components/tokens';

export interface InterruptedRunCardProps {
  /** Re-fire the user's last turn. Wired to ``retryInterruptedRun``. */
  onRetry: () => void;
  /** Disables the Retry button while the socket isn't ready to accept a send. */
  disabled?: boolean;
}

export const InterruptedRunCard = ({ onRetry, disabled }: InterruptedRunCardProps) => {
  const intl = useIntl();

  return (
    <Box sx={{ marginY: 2.5, marginX: { xs: 0, sm: 2 } }}>
      <Box
        data-testid="epi-chat-interrupted-run-card"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          padding: 4,
          backgroundColor: 'background.paper',
          borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`,
          borderRadius: `${borderRadius.sm}px`,
          boxShadow: shadows.sm,
          cursor: 'pointer',
          transition: 'box-shadow 0.15s',
          '&:hover': { boxShadow: shadows.md },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <ErrorOutlineRoundedIcon
            sx={{ fontSize: 20, color: 'error.main', flexShrink: 0 }}
            aria-hidden
          />
          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }} noWrap>
            {intl.formatMessage({
              id: 'epiChat.interruptedRun.chip',
              defaultMessage: 'Request interrupted',
            })}
          </Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {intl.formatMessage({
            id: 'epiChat.interruptedRun.body',
            defaultMessage:
              'Something interrupted this request before it finished. Retry to continue.',
          })}
        </Typography>

        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={onRetry}
          disabled={disabled}
          aria-label={intl.formatMessage({
            id: 'epiChat.interruptedRun.retryAriaLabel',
            defaultMessage: 'Retry the interrupted request',
          })}
          data-testid="epi-chat-interrupted-run-retry"
          sx={{ alignSelf: 'flex-start', marginTop: 2 }}
        >
          {intl.formatMessage({
            id: 'epiChat.interruptedRun.retryButton',
            defaultMessage: 'Retry',
          })}
        </Button>
      </Box>
    </Box>
  );
};
